const STORAGE_PREFIX = "ops.scheduleLastView.v1";
const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export function lastViewStorageKey(viewerKey, store, weekStart) {
  return `${STORAGE_PREFIX}:${String(viewerKey || "").trim()}:${String(store || "").trim()}:${String(weekStart || "").slice(0, 10)}`;
}

export function readLastViewedAt(viewerKey, store, weekStart) {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(lastViewStorageKey(viewerKey, store, weekStart));
    return raw && String(raw).trim() ? String(raw).trim() : null;
  } catch {
    return null;
  }
}

export function writeLastViewedAt(viewerKey, store, weekStart, at = new Date().toISOString()) {
  if (typeof localStorage === "undefined") return at;
  try {
    localStorage.setItem(lastViewStorageKey(viewerKey, store, weekStart), at);
  } catch {
    /* ignore quota / private mode */
  }
  return at;
}

export function changeRowTimestampMs(row) {
  const t = Date.parse(row?.changed_at || "");
  return Number.isFinite(t) ? t : 0;
}

export function isOwnScheduleChange(row, user) {
  const by = String(row?.changed_by_name || "").trim().toLowerCase();
  if (!by || !user) return false;
  const name = String(user?.name || "").trim().toLowerCase();
  const email = String(user?.email || "").trim().toLowerCase();
  return Boolean((name && by === name) || (email && by === email));
}

export function isOwnLocalScheduleChange(row, user) {
  if (String(row?.source || "local").toLowerCase() !== "local") return false;
  return isOwnScheduleChange(row, user);
}

export function unseenSlotKeyFromRow(row, weekDates = []) {
  const code = String(row?.employee_code || "").trim();
  const date = String(row?.rota_date || row?.date || "").slice(0, 10);
  if (code && /^\d{4}-\d{2}-\d{2}$/.test(date)) return `${code}::${date}`;
  const day = String(row?.day_key || "").toLowerCase().slice(0, 3);
  const idx = WEEKDAYS.indexOf(day);
  const fromWeek = idx >= 0 ? String(weekDates[idx] || "").slice(0, 10) : "";
  if (code && /^\d{4}-\d{2}-\d{2}$/.test(fromWeek)) return `${code}::${fromWeek}`;
  return "";
}

/** Slots that changed after lastViewedAt. First visit (no cursor) → []. */
export function computeUnseenScheduleSlots(rows, { lastViewedAt, weekDates, excludeOwnLocal, user } = {}) {
  if (!lastViewedAt) return [];
  const since = Date.parse(lastViewedAt);
  if (!Number.isFinite(since)) return [];
  const bySlot = new Map();
  (rows || []).forEach((row) => {
    if (changeRowTimestampMs(row) <= since) return;
    if (excludeOwnLocal && isOwnLocalScheduleChange(row, user)) return;
    const key = unseenSlotKeyFromRow(row, weekDates);
    if (!key) return;
    const current = bySlot.get(key) || { key, changes: [] };
    current.changes.push(row);
    bySlot.set(key, current);
  });
  return [...bySlot.values()]
    .map((entry) => {
      const latest = entry.changes.slice().sort((a, b) => changeRowTimestampMs(b) - changeRowTimestampMs(a))[0];
      return {
        key: entry.key,
        employee_code: String(latest?.employee_code || "").trim(),
        employee_name: latest?.employee_name || "",
        date: entry.key.split("::")[1] || "",
        day_key: latest?.day_key || "",
        count: entry.changes.length,
        latest,
      };
    })
    .sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(a.employee_name).localeCompare(String(b.employee_name)));
}

export function isPendingReviewStatus(status) {
  const st = String(status || "").toLowerCase();
  return st === "pending" || st === "sent";
}

/** One net change per employee + day. Oldest old_value → newest new_value. */
export function collapseScheduleChangeRows(rows, weekDates = []) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = unseenSlotKeyFromRow(row, weekDates) || (row?.id != null ? `id:${row.id}` : "");
    if (!key) return;
    const current = map.get(key) || { key, rows: [] };
    current.rows.push(row);
    map.set(key, current);
  });
  return [...map.values()].map(({ key, rows: group }) => {
    const sorted = group.slice().sort((a, b) => changeRowTimestampMs(a) - changeRowTimestampMs(b));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const oldValue = first?.old_value ?? "";
    const newValue = last?.new_value ?? "";
    let changeType = "modified";
    if (!oldValue && newValue) changeType = "assigned";
    else if (oldValue && !newValue) changeType = "removed";
    const statuses = group.map((r) => String(r.hr_status || "").toLowerCase());
    const hrStatus = statuses.some((s) => s === "pending")
      ? "pending"
      : statuses.some((s) => s === "sent")
        ? "sent"
        : "confirmed";
    return {
      key,
      employee_code: String(last?.employee_code || first?.employee_code || "").trim(),
      employee_name: last?.employee_name || first?.employee_name || "",
      day_key: last?.day_key || first?.day_key || "",
      rota_date: String(last?.rota_date || first?.rota_date || key.split("::")[1] || "").slice(0, 10),
      change_type: changeType,
      old_value: oldValue || null,
      new_value: newValue || null,
      changed_at: last?.changed_at,
      changed_by_name: last?.changed_by_name,
      hr_status: hrStatus,
      ids: group.map((r) => r.id).filter((id) => id != null),
      count: group.length,
      rows: sorted,
    };
  });
}

/** Pending/sent only if someone else still needs review — not the viewer's own edits. */
export function slotNeedsPendingReviewForUser(slot, user) {
  const rows = slot?.rows;
  if (Array.isArray(rows) && rows.length) {
    return rows.some(
      (row) => isPendingReviewStatus(row.hr_status) && !isOwnScheduleChange(row, user)
    );
  }
  if (!isPendingReviewStatus(slot?.hr_status)) return false;
  return !isOwnScheduleChange(slot, user);
}

export function viewerChangeReviewStatus(slot, user) {
  if (!slotNeedsPendingReviewForUser(slot, user)) {
    return isPendingReviewStatus(slot?.hr_status) ? "own" : String(slot?.hr_status || "confirmed").toLowerCase();
  }
  return String(slot?.hr_status || "pending").toLowerCase();
}

export function groupCollapsedChangesByEmployee(collapsed, user) {
  const map = new Map();
  (collapsed || []).forEach((slot) => {
    const empKey = String(slot.employee_code || slot.employee_name || "unknown").trim().toLowerCase();
    const current = map.get(empKey) || {
      employee_code: slot.employee_code || "",
      employee_name: slot.employee_name || "",
      slots: [],
    };
    current.slots.push(slot);
    if (!current.employee_name && slot.employee_name) current.employee_name = slot.employee_name;
    map.set(empKey, current);
  });
  return [...map.values()]
    .map((group) => {
      const slots = group.slots.slice().sort((a, b) => String(a.rota_date || "").localeCompare(String(b.rota_date || "")));
      const pendingSlots = slots.filter((s) => slotNeedsPendingReviewForUser(s, user));
      return {
        ...group,
        slots,
        pendingCount: pendingSlots.length,
        pendingIds: pendingSlots.flatMap((s) =>
          (s.rows || [])
            .filter((row) => isPendingReviewStatus(row.hr_status) && !isOwnScheduleChange(row, user))
            .map((row) => row.id)
            .filter((id) => id != null)
        ),
      };
    })
    .sort((a, b) => String(a.employee_name || a.employee_code).localeCompare(String(b.employee_name || b.employee_code)));
}
